import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";
import { actionKey, type ActionDefinition, runAction } from "./defineAction";
import {
  dispatch,
  getActionSnapshot,
  getAttempt,
  getServerActionSnapshot,
  setViewer,
  subscribe,
} from "./store";
import { type ActionStatus, type Attempt, isPendingStatus } from "./types";

export interface ActionView {
  /** `null` means idle — no attempt for this key. */
  status: ActionStatus | null;
  /** Work is genuinely outstanding. Drive busy affordances from this. */
  isPending: boolean;
  /** Taking longer than usual. Presentation only; the attempt is still alive. */
  isSlow: boolean;
  error: string | null;
  /** Start (or restart) the action. */
  run: () => void;
  /** Dismiss a settled outcome so the control returns to idle. */
  acknowledge: () => void;
}

const EMPTY_ATTEMPT: Attempt | null = null;

export function useActionSnapshot() {
  return useSyncExternalStore(
    subscribe,
    getActionSnapshot,
    getServerActionSnapshot,
  );
}

function useAttempt(key: string): Attempt | null {
  const getSnapshot = useCallback(() => getAttempt(key), [key]);
  // `getAttempt` returns the stored reference, which only changes when the
  // attempt actually changes — so this is a stable snapshot, not a new object
  // per render. The server snapshot is always "no attempt": the store is
  // module-level in-memory state that starts empty, so both agree.
  return useSyncExternalStore(subscribe, getSnapshot, () => EMPTY_ATTEMPT);
}

/**
 * Read one action's status for one target. Safe to call anywhere in the tree —
 * a row, a header badge, a different page. Everyone gets the same answer,
 * because there is one store rather than one `useState` per screen.
 */
export function useActionStatus<TInput>(
  definition: ActionDefinition<TInput>,
  input: TInput,
): ActionView {
  const key = actionKey(definition, input);
  const attempt = useAttempt(key);

  const run = useCallback(() => {
    void runAction(definition, input);
    // `input` is compared by the caller's own memoisation; actions are keyed by
    // their target string, so a fresh object with the same ids is equivalent.
  }, [definition, input]);

  const acknowledge = useCallback(() => {
    dispatch(key, { type: "ACKNOWLEDGE" });
  }, [key]);

  return useMemo(
    () => ({
      status: attempt?.status ?? null,
      isPending: attempt ? isPendingStatus(attempt.status) : false,
      isSlow: attempt?.slow ?? false,
      error: attempt?.error ?? null,
      run,
      acknowledge,
    }),
    [attempt, run, acknowledge],
  );
}

/**
 * The value an outstanding attempt is asserting for this target, or undefined.
 *
 * Merge it over the query's value at render — `optimisticName ?? row.filename`
 * — rather than writing it into the cache. Server truth underneath is never
 * touched, so there is nothing to roll back and no way for one attempt's
 * optimism to contaminate another's idea of what the "previous" value was
 * (ACTION_PATTERN.md §4.8).
 */
export function useActionOptimistic<TInput, TValue>(
  definition: ActionDefinition<TInput>,
  input: TInput,
): TValue | undefined {
  const key = actionKey(definition, input);
  const snapshot = useActionSnapshot();
  return snapshot.optimistic.get(key) as TValue | undefined;
}

/**
 * How many actions are outstanding, optionally for one action name. For trays,
 * counters and "should I keep polling?" — NEVER for a `disabled` prop. An
 * aggregate feeding a disabled state is exactly how generating one summary
 * greys out an unrelated button.
 */
export function useActiveActionCount(actionName?: string): number {
  const snapshot = useActionSnapshot();
  let count = 0;
  for (const [key, attempt] of snapshot.attempts) {
    if (
      isPendingStatus(attempt.status) &&
      (!actionName || key.includes(`::${actionName}:`))
    )
      count++;
  }
  return count;
}

/**
 * Scope the store to the effective viewer. Impersonation changes at runtime
 * with no reload, so without this an admin switching who they view as would
 * inherit the previous viewer's pending work — with its poll still running as
 * somebody else. Mount once, high in the tree.
 */
export function useActionViewerScope(effectiveUserId: string | null): void {
  useEffect(() => {
    setViewer(effectiveUserId ?? "anonymous");
  }, [effectiveUserId]);
}
